/**
 * TowerEntryModal.jsx - Tower entry confirmation modal
 *
 * Shows tower info before entering:
 * - Tower name, icon, and description
 * - Floor count and difficulty tier
 * - Expected enemies and loot
 * - Special mechanics (stairs, checkpoints)
 */

import React from 'react';
import { TOWER_BIOMES } from '../../data/world-map/towerBiomes';
import { DUNGEON_CONFIG, LOOT_TABLES, ENEMY_TEMPLATES } from '../../data/dungeon';
import './TowerEntryModal.css';

const TowerEntryModal = ({ biomeId, playerLevel, currentEra, cooldownRemaining, onEnter, onCancel }) => {
  if (!biomeId) return null;

  const tower = TOWER_BIOMES[biomeId];
  if (!tower || !tower.isTowerBiome) return null;

  // Check if tower is locked by era
  const isEraLocked = (currentEra || 1) < tower.minAge;

  // Get tier info for enemy scaling
  const tierInfo = DUNGEON_CONFIG.TIER_INFO[tower.dungeonTier];

  // Get loot table info
  const lootTable = LOOT_TABLES[tower.dungeonLootTable] || LOOT_TABLES.common;
  const rarityWeights = lootTable.rarityWeights;

  // Determine difficulty based on player level vs recommended level
  const recommendedLevel = tierInfo?.levelRange?.[0] || 5;
  const levelDiff = playerLevel - recommendedLevel;
  let difficultyColor = '#f59e0b'; // Amber (even match)
  let difficultyText = 'Recommended';

  if (levelDiff >= 15) {
    difficultyColor = '#22c55e'; // Green (easy)
    difficultyText = 'Easy';
  } else if (levelDiff >= 8) {
    difficultyColor = '#a3e635'; // Light green (manageable)
    difficultyText = 'Manageable';
  } else if (levelDiff <= -10) {
    difficultyColor = '#dc2626'; // Red (very hard)
    difficultyText = 'Very Hard';
  } else if (levelDiff <= -5) {
    difficultyColor = '#f97316'; // Orange (hard)
    difficultyText = 'Hard';
  }

  // Format cooldown time
  const formatCooldown = (ms) => {
    if (!ms || ms <= 0) return null;
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const cooldownDisplay = formatCooldown(cooldownRemaining);
  const isOnCooldown = cooldownRemaining && cooldownRemaining > 0;

  // Show locked state for era requirement
  if (isEraLocked) {
    return (
      <div className="modal-overlay" onClick={onCancel}>
        <div className="modal-content tower-entry-modal" onClick={(e) => e.stopPropagation()}>
          <div className="tem-header">
            <div className="tem-title">
              <span className="tem-icon">{tower.icon}</span>
              <div className="tem-title-text">
                <h2>{tower.name}</h2>
                <p className="tem-subtitle">Era {tower.minAge} Required</p>
              </div>
            </div>
            <button className="close-btn" onClick={onCancel}>×</button>
          </div>
          <div className="tem-body">
            <div className="tem-desc" style={{ textAlign: 'center', color: '#c06060' }}>
              You must reach Era {tower.minAge} to enter this tower.
              <br />
              <span style={{ color: '#9a8e7a' }}>Current Era: {currentEra || 1}</span>
            </div>
            <div className="tem-desc" style={{ textAlign: 'center', marginTop: '0.5rem', padding: '0.5rem', background: 'rgba(128, 184, 88, 0.15)', borderRadius: '4px', borderLeft: '3px solid var(--success)' }}>
              <span style={{ color: 'var(--success)' }}>💡 Tip:</span>{' '}
              <span style={{ color: 'var(--text-secondary)' }}>
                Research higher tier technologies to advance your era!
              </span>
            </div>
          </div>
          <div className="tem-footer">
            <button className="tem-btn tem-btn-cancel" onClick={onCancel}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const handleEnter = () => {
    if (!isOnCooldown) {
      onEnter(biomeId);
    }
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content tower-entry-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="tem-header">
          <div className="tem-title">
            <span className="tem-icon">{tower.icon}</span>
            <div className="tem-title-text">
              <h2>{tower.name}</h2>
              <p className="tem-subtitle">{tower.towerFloors} Floor Tower</p>
            </div>
          </div>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>

        {/* Body */}
        <div className="tem-body">
          {/* Description */}
          <div className="tem-desc">{tower.description}</div>

          {/* Stats Grid - 5 columns */}
          <div className="tem-stats-grid">
            <div className="tem-stat">
              <div className="tem-stat-label">Floors</div>
              <div className="tem-stat-value floors">{tower.towerFloors}</div>
            </div>
            <div className="tem-stat">
              <div className="tem-stat-label">Tier</div>
              <div className="tem-stat-value">{tierInfo?.name || tower.dungeonTier}</div>
            </div>
            <div className="tem-stat">
              <div className="tem-stat-label">Enemy Level</div>
              <div className="tem-stat-value" style={{ color: difficultyColor }}>{recommendedLevel}</div>
            </div>
            <div className="tem-stat">
              <div className="tem-stat-label">Your Level</div>
              <div className="tem-stat-value">{playerLevel}</div>
            </div>
            <div className="tem-stat">
              <div className="tem-stat-label">Difficulty</div>
              <div className="tem-stat-value" style={{ color: difficultyColor }}>{difficultyText}</div>
            </div>
          </div>

          {/* Tower Features */}
          <div className="tem-features">
            <div className="tem-section-header">🗼 Tower Features</div>
            <div className="tem-feature-grid">
              <div className="tem-feature">
                <span className="tem-feature-icon">⬇️</span>
                <span className="tem-feature-text">Stairs to descend floors</span>
              </div>
              <div className="tem-feature">
                <span className="tem-feature-icon">⬆️</span>
                <span className="tem-feature-text">Return to previous floors</span>
              </div>
              <div className="tem-feature">
                <span className="tem-feature-icon">🏳️</span>
                <span className="tem-feature-text">Checkpoints: Rest or Exit</span>
              </div>
              <div className="tem-feature">
                <span className="tem-feature-icon">👑</span>
                <span className="tem-feature-text">Boss on final floor only</span>
              </div>
            </div>
          </div>

          {/* Enemies & Loot - 2 column grid */}
          <div className="tem-details-grid">
            {/* Enemies */}
            <div className="tem-section">
              <div className="tem-section-header">Enemies ({tower.dungeonMobPool.length})</div>
              <div className="tem-mob-grid">
                {tower.dungeonMobPool.map((mobId, idx) => {
                  const mob = ENEMY_TEMPLATES[mobId];
                  return (
                    <div key={idx} className="tem-mob" title={mob?.name || mobId}>
                      <span className="tem-mob-icon">{mob?.icon || '👹'}</span>
                      <span className="tem-mob-name">{mob?.name || mobId}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Loot */}
            <div className="tem-section">
              <div className="tem-section-header">Loot: {tower.dungeonLootTable.charAt(0).toUpperCase() + tower.dungeonLootTable.slice(1)}</div>
              <div className="tem-loot-grid">
                {Object.entries(rarityWeights).map(([rarity, weight]) => {
                  if (weight === 0) return null;

                  let rarityColor = '#9a8e7a'; // Gray for common
                  if (rarity === 'uncommon') rarityColor = '#80b858';
                  if (rarity === 'rare') rarityColor = '#6080a8';
                  if (rarity === 'epic') rarityColor = '#806890';
                  if (rarity === 'legendary') rarityColor = '#dab050';
                  if (rarity === 'mythic') rarityColor = '#ff6b9d';

                  return (
                    <div key={rarity} className="tem-loot-row">
                      <span className="tem-loot-name" style={{ color: rarityColor }}>
                        {rarity.charAt(0).toUpperCase() + rarity.slice(1)}
                      </span>
                      <div className="tem-loot-bar-wrap">
                        <div className="tem-loot-bar" style={{ width: `${weight}%`, backgroundColor: rarityColor }} />
                      </div>
                      <span className="tem-loot-pct">{weight}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Cooldown Warning */}
          {isOnCooldown && (
            <div className="tem-cooldown-warning">
              <span className="tem-cooldown-icon">⏱️</span>
              <span className="tem-cooldown-text">Cooldown: {cooldownDisplay}</span>
            </div>
          )}

          {/* Info Banner */}
          <div className="tem-info-banner">
            <span className="tem-info-icon">💡</span>
            <span className="tem-info-text">
              Floor progress is saved! Use checkpoints to rest (+50% HP) or exit safely with your loot.
            </span>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="tem-footer">
          <button className="tem-btn tem-btn-cancel" onClick={onCancel}>Cancel</button>
          <button
            className={`tem-btn tem-btn-enter ${isOnCooldown ? 'disabled' : ''}`}
            onClick={handleEnter}
            disabled={isOnCooldown}
          >
            {isOnCooldown ? `⏱️ ${cooldownDisplay}` : `🗼 Enter ${tower.name}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TowerEntryModal;
